import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Pinned conversations — a DEVICE preference, and the comment is here so nobody mistakes it for
 * a server feature.
 *
 * `ChatList` requires `onTogglePin`; the chat contract (§14b) has no pin field, no pin endpoint
 * and no pin in the conversation DTO. The three honest options were: hide the control, send the
 * pin somewhere it would be ignored, or keep it on the device. The first removes a rail feature
 * the kit was built around, the second is a control that silently does nothing on the user's
 * other machine. So it is stored locally, next to the theme and the sidebar state, which are the
 * other two preferences in this application that belong to a browser rather than to an account.
 *
 * THE CONSEQUENCE IS STATED IN THE UI's OWN TERMS: a pin does not travel to another device. When
 * the server grows a pin, this file is deleted and the flag comes off the row.
 *
 * Every read and write is wrapped, because `localStorage` throws outright in a browser set to
 * block site data and in a private window under some engines. A rail that fails to render
 * because a preference could not be read would be a far worse bug than a pin that is forgotten.
 */

const STORAGE_KEY = "altek.chat.pinned";

function read(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function write(ids: string[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Nothing to report and nothing to retry: the preference simply does not persist here.
  }
}

export interface PinnedConversations {
  isPinned: (id: string) => boolean;
  toggle: (id: string) => void;
}

export function usePinnedConversations(): PinnedConversations {
  const [ids, setIds] = useState<string[]>([]);

  // Read after mount rather than in the initialiser: the value is per-browser, so reading it
  // during render would make the first paint depend on storage that may be unavailable.
  useEffect(() => setIds(read()), []);

  const toggle = useCallback((id: string) => {
    setIds((current) => {
      const next = current.includes(id)
        ? current.filter((candidate) => candidate !== id)
        : [...current, id];
      write(next);
      return next;
    });
  }, []);

  const isPinned = useCallback((id: string) => ids.includes(id), [ids]);

  // Aynı nesne, sabitlemeler değişmedikçe: listenin satırlarını kuran `useMemo` bu nesneye bağlı ve
  // her çizimde yeni bir nesne, satırları her çizimde yeniden kurduruyordu (denetim Sohbet 15).
  return useMemo(() => ({ isPinned, toggle }), [isPinned, toggle]);
}
